import React, { useState, useCallback, useRef, useMemo, memo, useContext, lazy, Suspense } from 'react';
import { Box, Typography, IconButton, Tooltip, CircularProgress, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew'; // Đã thêm icon này
import BusinessIcon from '@mui/icons-material/Business';

import { useHistory } from 'react-router-dom';
import AuthContext from './auth/Context';

import DashboardWidget from './DashboardWidget';
import DashboardReportWidget from './DashboardReportWidget';
import DataListManager from './DataListManager';
import CounterWidget from './Counter'; 
import SoDemLoiTat from './SoDemLoiTat';
import PhamViCongTy, { DemNhieuCongTy, useTenCongTy, LocCongTyContext } from './PhamViCongTy';
import { dsCongTyHieuLuc, canPhamViCongTyHieuLuc, khoaChuyenTrang } from './dashboardCongTy';
import { taoTrangThaiMoBaoCao } from './moBaoCaoTuDashboard';
import WorkflowViewer from './WorkflowViewer'; 
import FlowEditor from './flow/FlowEditor'; 
// Nạp TRỄ: (1) dashboard không có widget 'custom' thì không kéo theo bộ biên dịch trang tuỳ biến;
// (2) tránh thêm một vòng import mới — DynamicCustomPage -> DataQuickActionContext -> … -> file này.
const DynamicCustomPage = lazy(() => import('./DynamicCustomPage'));

import { SHORTCUT_ICONS, WIDGET_COLORS } from './DynamicWidgetConfigs';
import { useQuickAction } from './DataQuickActionContext';
import { useQuery } from '@tanstack/react-query';

// CSS Tùy chỉnh thanh cuộn siêu mỏng chuẩn macOS
const macScrollbarSx = {
  '&::-webkit-scrollbar': { width: '5px', height: '5px' },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { 
    backgroundColor: 'rgba(0,0,0,0.15)', 
    borderRadius: '10px',
    transition: 'background-color 0.3s'
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.3)' },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0,0,0,0.15) transparent'
};

// 👇 KHAI BÁO CSS CONTAINER QUERY
const SMALL_WIDGET_QUERY = '@container (max-width: 260px)';

// ================= DỮ LIỆU NHIỀU CÔNG TY (widgetConfig.id_apps — xem dashboardCongTy.js) =================
// Các prop dưới đây chỉ do WidgetTheoCongTy (cuối file) truyền vào:
//   ngoaiCongTy   widget KHÔNG hiện dữ liệu của (riêng) công ty đang đăng nhập -> khoá chuyển trang
//                 (trang /page/... luôn mở theo công ty đang đăng nhập — mở ra sẽ là dữ liệu khác).
//   nhanCongTy    nhãn công ty hiện cạnh tiêu đề.
//   thanhCongTy   thanh chọn công ty (widget nhiều công ty, không phải counter).
//   dsCongTyTong  counter cộng tổng các công ty này.
//   trangThaiCongTy  { dangTai } | { loi } — đang đọc / không đọc được thông tin công ty đã chọn.
const WidgetDispatcher = ({ widget, onRemove, isEditMode, ngoaiCongTy = false, nhanCongTy = '', thanhCongTy = null, dsCongTyTong = null, trangThaiCongTy = null }) => {
  const { type, title, widgetConfig={} } = widget;
  const history = useHistory();
  const { openAdd } = useQuickAction();
  const context = useContext(AuthContext);

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [resolvedMeta, setResolvedMeta] = useState(null);
  const refList = useRef();
  // ReportFrame của widget pivot/report — đọc điều kiện ĐANG hiển thị khi mở trang báo cáo gốc.
  const refBaoCao = useRef(null);

  const handleMetaLoaded = useCallback((meta) => {
    setResolvedMeta(meta);
  }, []);

  const isListAddAction = type === 'list' && widgetConfig.action === 'add';

  // ================= CẤU HÌNH CỦA WIDGET DANH SÁCH =================
  //
  // `DataListManager` KHÔNG tự đi tìm cấu hình của nó: ai dựng nó thì người đó phải đưa `fields`
  // vào. Trang đầy đủ `/page/list/<code>` làm việc đó; widget này thì trước đây không — nó chỉ
  // truyền `{code, condition}`. `fields` rỗng dẫn tới HAI triệu chứng trông như lỗi dữ liệu:
  // danh sách hiện nhãn mặc định "Bản ghi #N" (không có field nào để làm tiêu đề), và bấm vào
  // thì form mở ra TRẮNG (không có ô nào để vẽ). Dữ liệu vẫn đủ, chỉ là không ai bảo cách hiển thị.
  //
  // Dùng CHUNG hàm nạp của trang đầy đủ (`napCauHinhDanhSach`) chứ không chép lại: hai cách nạp
  // song song là cách chắc chắn nhất để cùng một module hiện một kiểu ở trang và một kiểu ở đây.
  // Nạp trễ để không thêm một vòng import giữa hai file.
  const maDanhSach = (type === 'list' && !isListAddAction) ? (widgetConfig.moduleCode || widgetConfig.code || '') : '';
  const userToken = context?.userInfo?.token || (typeof context?.getToken === 'function' ? context.getToken() : '');
  const { data: cauHinhDanhSach } = useQuery({
    queryKey: ['dataList_config', maDanhSach],
    queryFn: async () => {
      const { napCauHinhDanhSach } = await import('./DataDynamicPage');
      return napCauHinhDanhSach({ code: maDanhSach, type: 'list', context, userToken });
    },
    enabled: !!maDanhSach && !!userToken,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const isShortcutLike = type === 'shortcut' || type === 'counter'  || type === 'workflow'  || type === 'flow' || isListAddAction;

  // ================= 1. XỬ LÝ CHUYỂN TRANG / HÀNH ĐỘNG =================
  const handleNavigate = useCallback(() => {
    if (isEditMode) return;
    if (ngoaiCongTy) {
      if (context?.alert) context.alert(`Widget đang hiện dữ liệu của ${nhanCongTy || 'công ty khác'}. Đổi sang công ty đó để mở trang chi tiết.`, null, 'info');
      return;
    }

    if (isListAddAction) {
      const code = widgetConfig.moduleCode || widgetConfig.code;
      openAdd(code, [], title, {}, () => {});
      return;
    }

    const mergedConfig = { ...widgetConfig, ...resolvedMeta };
    let targetUrl = mergedConfig.url;

    if (!targetUrl) {
      const code = mergedConfig.moduleCode || mergedConfig.code;
      if (code && (type === 'list' || type === 'pivot' || type === 'report' || type === 'workflow' || type === 'custom')){
        if(type === "pivot" || type === "report"){
          targetUrl = `/page/report/${code}`;
        } else {
          // 'custom' -> /page/custom/<code>: bấm tiêu đề widget để mở trang đầy đủ.
          targetUrl = `/page/${type}/${code}`;
        }
      }
    }

    if (targetUrl) {
      if (targetUrl.startsWith('http')) {
        window.open(targetUrl, '_blank');
        return;
      }
      const combinedCondition = { ...(mergedConfig.condition || {}), ...(mergedConfig.reportCondition || {}) };
      // pivot/report: mở đúng mẫu pivot + điều kiện widget đang hiển thị (xem moBaoCaoTuDashboard.js).
      const dieuKienDangHien = refBaoCao.current?.getCondition ? refBaoCao.current.getCondition() : null;
      history.push({ pathname: targetUrl, state: taoTrangThaiMoBaoCao({ type, widgetConfig, dieuKienKhaiBao: combinedCondition, dieuKienDangHien }) });
    } else {
      console.warn("Chưa cấu hình URL chuyển trang.");
    }
  }, [isEditMode, ngoaiCongTy, nhanCongTy, context, isListAddAction, widgetConfig, resolvedMeta, type, history, openAdd, title]);

  // ================= TÍNH TOÁN THEME & MÀU SẮC =================
  const themeVars = useMemo(() => {
    const isSeamlessCardMode = type === 'pivot' && resolvedMeta?.displayMode === 'card';
    const isGradientTheme = isShortcutLike && typeof widgetConfig.color === 'string' && widgetConfig.color.includes('gradient');
    const isWhiteTheme = isShortcutLike && widgetConfig.color === '#ffffff';
    const isCustomBg = isGradientTheme || isWhiteTheme;

    const matchedColorDef = WIDGET_COLORS.find(c => c.value === widgetConfig.color);
    const themeTextColor = matchedColorDef?.textColor || (isGradientTheme ? '#1f2937' : 'inherit');
    const themePrimaryColor = isCustomBg ? themeTextColor : (widgetConfig.color || '#007aff');
    const iconBgColor = isGradientTheme 
      ? 'rgba(255,255,255,0.25)'         
      : (isWhiteTheme ? 'rgba(0,0,0,0.04)' : 'rgba(0,122,255,0.08)'); 

    return { isSeamlessCardMode, isGradientTheme, isCustomBg, themeTextColor, themePrimaryColor, iconBgColor };
  }, [type, resolvedMeta?.displayMode, isShortcutLike, widgetConfig.color]);

  // ================= 2. HÀM RENDER "RUỘT" WIDGET =================
  const renderWidgetContent = () => {
    if (trangThaiCongTy?.dangTai) {
      return <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={22} /></Box>;
    }
    if (trangThaiCongTy?.loi) {
      return (
        <Box sx={{ p: 2, color: 'error.main' }}>
          <Typography variant="body2">Không xem được dữ liệu {nhanCongTy || 'công ty này'}: {trangThaiCongTy.loi}</Typography>
        </Box>
      );
    }
    switch (type) {
      case 'pivot':
        return (
          <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'auto' }}>
            <DashboardWidget pivotConfigId={widgetConfig.pivotConfigId} refreshTrigger={refreshTrigger} onMetaLoaded={handleMetaLoaded} frameRef={refBaoCao} />
          </Box>
        );

      case 'report':
        return (
          <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'auto' }}>
            <DashboardReportWidget code={widgetConfig.code} condition={widgetConfig.condition} refreshTrigger={refreshTrigger} frameRef={refBaoCao} />
          </Box>
        );

      case 'workflow':
        return (
          <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'auto', p: 0 }}>
            <WorkflowViewer code={widgetConfig.code} containerSx={{ height: '100%', borderRadius: 1 }} />
          </Box>
        );
      case "flow":
        return (
          <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'auto', p: 0 }}>
            <FlowEditor viewOnly={true} initialFlowId={widgetConfig.flowId} apiUrl={context.config.server_url} idApp={context.appInfo?._id} accessToken={context.userInfo?.token} containerSx={{ height: '100%', borderRadius: 1 }} />
          </Box>
          
        )
        
      // TRANG TUỲ BIẾN nhúng thẳng vào dashboard. Dùng LẠI DynamicCustomPage nên script chạy trong
      // widget với đúng bộ "đồ nghề" như khi chạy trang đầy đủ (React, MUI, context, quickAction,
      // apis, socket…) — không có sandbox thứ hai để lệch nhau.
      // `key` gắn refreshTrigger: nút Làm mới remount trang -> script tự chạy lại phần nạp dữ liệu.
      case 'custom':
        return (
          <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{ height: '100%', overflow: 'auto', cursor: 'auto', ...macScrollbarSx }}>
            <Suspense fallback={<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={22} /></Box>}>
              <DynamicCustomPage
                key={`cp_${widgetConfig.code}_${refreshTrigger}`}
                code={widgetConfig.code}
                embedded
                history={history}
                location={history && history.location}
              />
            </Suspense>
          </Box>
        );

      case 'list': {
        if (isListAddAction) {
          return (
            <Box sx={{ 
              display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', 
              p: 2, gap: 2, flexDirection: 'row', textAlign: 'left',
              [SMALL_WIDGET_QUERY]: { flexDirection: 'column', p: 1, gap: 1, textAlign: 'center' }
            }}>
               <Box sx={{ 
                 flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: themeVars.iconBgColor,
                 width: 48, height: 48,
                 [SMALL_WIDGET_QUERY]: { width: 40, height: 40 }
               }}>
                 <AddIcon sx={{ 
                   color: themeVars.themePrimaryColor, fontSize: 26,
                   [SMALL_WIDGET_QUERY]: { fontSize: 22 } 
                 }} />
               </Box>
               <Box sx={{ 
                 display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, justifyContent: 'center', alignItems: 'flex-start',
                 [SMALL_WIDGET_QUERY]: { alignItems: 'center' }
               }}>
                 <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                   lineHeight: 1.2, color: themeVars.isCustomBg ? themeVars.themeTextColor : 'inherit', display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden', 
                   WebkitLineClamp: 2, fontSize: '1rem',
                   [SMALL_WIDGET_QUERY]: { fontSize: '0.85rem' }
                 }}>
                    {title}
                 </Typography>
                 <Typography variant="caption" sx={{ 
                   color: themeVars.isCustomBg ? themeVars.themeTextColor : 'text.secondary', opacity: 0.8, mt: 0.5, display: 'block',
                   [SMALL_WIDGET_QUERY]: { display: 'none' } 
                 }}>
                   Mở form nhập liệu
                 </Typography>
               </Box>
            </Box>
          );
        }

        // Cấu hình của module trước, khai báo riêng của widget sau — widget được quyền thu hẹp
        // (điều kiện lọc, khoá thêm/xoá, sắp xếp mặc định) nhưng không phải khai lại `fields`.
        let finalPageConfig = { ...(cauHinhDanhSach || {}), ...widgetConfig };
        finalPageConfig.canAdd = finalPageConfig.canAdd !== false;
        finalPageConfig.canEdit = finalPageConfig.canEdit !== false;
        finalPageConfig.canDelete = finalPageConfig.canDelete !== false;
        if (finalPageConfig.sortOptions?.length === 0) finalPageConfig.sortOptions = undefined;
        const mergedCondition = { ...(finalPageConfig.defaultCondition || {}), ...(finalPageConfig.condition || {}) };
        
        return (
          <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} sx={{ height: '100%', overflow: 'auto', p: 0, cursor: 'auto', ...macScrollbarSx }}>
            <DataListManager 
              key={`page_${finalPageConfig.code || finalPageConfig.moduleCode}`} {...finalPageConfig} 
              hideHeader={true}
              defaultViewMode="compact"
              ref={refList} 
              defaultCondition={mergedCondition} 
              containerSx={{ borderRadius: 0, height: "100%" }} />
          </Box>
        );
      }

      case 'counter': {
        const counterIconDef = SHORTCUT_ICONS.find(i => i.value === widgetConfig.icon) || SHORTCUT_ICONS[0];
        const CounterIcon = counterIconDef.icon;
        
        return (
          <Box sx={{ 
            display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', 
            p: 2, gap: 2, flexDirection: 'row', textAlign: 'left',
            [SMALL_WIDGET_QUERY]: { flexDirection: 'column', p: 1, gap: 0.5, textAlign: 'center' }
          }}>
             <Box sx={{ 
               flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: themeVars.iconBgColor,
               width: 52, height: 52,
               [SMALL_WIDGET_QUERY]: { width: 40, height: 40 }
             }}>
               <CounterIcon sx={{ 
                 color: themeVars.themePrimaryColor, fontSize: 28,
                 [SMALL_WIDGET_QUERY]: { fontSize: 22 }
               }} />
             </Box>
             <Box sx={{ 
               display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, justifyContent: 'center', alignItems: 'flex-start',
               [SMALL_WIDGET_QUERY]: { alignItems: 'center' }
             }}>
               <Typography variant="h4" fontWeight="800" sx={{ 
                 color: themeVars.themePrimaryColor, lineHeight: 1, mb: 0.25, fontSize: '2.125rem',
                 [SMALL_WIDGET_QUERY]: { fontSize: '1.5rem', mb: 0 }
               }}>
                  {dsCongTyTong ? (
                    <DemNhieuCongTy dsCongTy={dsCongTyTong} code={widgetConfig.apiCode} condition={widgetConfig.condition} refreshTrigger={refreshTrigger} renderRunning={() => <Typography variant="h4" color="inherit">...</Typography>} />
                  ) : (
                    <CounterWidget code={widgetConfig.apiCode} condition={widgetConfig.condition} refreshTrigger={refreshTrigger} renderRunning={() => <Typography variant="h4" color="inherit">...</Typography>} />
                  )}
               </Typography>
               <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                 color: themeVars.isCustomBg ? themeVars.themeTextColor : 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                 fontSize: '0.7rem',
                 [SMALL_WIDGET_QUERY]: { fontSize: '0.65rem' }
               }}>
                  {title}
               </Typography>
               {!!nhanCongTy && (
                 <Typography variant="caption" noWrap title={nhanCongTy} sx={{
                   color: themeVars.isCustomBg ? themeVars.themeTextColor : 'text.secondary', opacity: 0.75, fontSize: '0.65rem', maxWidth: '100%',
                   display: 'inline-flex', alignItems: 'center', gap: 0.4
                 }}>
                   <BusinessIcon sx={{ fontSize: '0.75rem' }} />{nhanCongTy}
                 </Typography>
               )}
             </Box>
          </Box>
        );
      }
      
      case 'shortcut': {
        const iconDef = SHORTCUT_ICONS.find(i => i.value === widgetConfig.icon) || SHORTCUT_ICONS[0];
        const RenderIcon = iconDef.icon;
        
        return (
          <Box sx={{ 
            display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', 
            p: 2, gap: 2, flexDirection: 'row', textAlign: 'left',
            [SMALL_WIDGET_QUERY]: { flexDirection: 'column', p: 1, gap: 1, textAlign: 'center' }
          }}>
             <Box sx={{ 
               flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: themeVars.iconBgColor,
               width: 48, height: 48, position: 'relative',
               [SMALL_WIDGET_QUERY]: { width: 40, height: 40 }
             }}>
               <RenderIcon sx={{ 
                 color: themeVars.themePrimaryColor, fontSize: 26,
                 [SMALL_WIDGET_QUERY]: { fontSize: 22 }
               }} />
               {/* SỐ ĐẾM trên lối tắt (widgetConfig.badge) — vd số tin nhắn chưa đọc của hộp thư. Không khai thì không vẽ gì. */}
               {!isEditMode && widgetConfig.badge ? <SoDemLoiTat widgetConfig={widgetConfig} /> : null}
             </Box>
             <Box sx={{ 
               display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, justifyContent: 'center', alignItems: 'flex-start',
               [SMALL_WIDGET_QUERY]: { alignItems: 'center' }
             }}>
               <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                 lineHeight: 1.2, color: themeVars.isCustomBg ? themeVars.themeTextColor : 'inherit', display: '-webkit-box', WebkitBoxOrient: 'vertical', overflow: 'hidden', 
                 fontSize: '1rem', WebkitLineClamp: 2,
                 [SMALL_WIDGET_QUERY]: { fontSize: '0.8rem', WebkitLineClamp: 3 }
               }}>
                  {title}
               </Typography>
             </Box>
          </Box>
        );
      }
      default:
        return <Box sx={{ p: 2, color: 'error.main' }}>Loại Widget không hỗ trợ</Box>;
    }
  };

  return (
    <Box 
      onClick={handleNavigate}
      sx={{ 
        containerType: 'inline-size',
        display: 'flex', flexDirection: 'column', height: '100%', position: 'relative',
        bgcolor: themeVars.isSeamlessCardMode ? 'transparent' : (themeVars.isCustomBg ? 'transparent' : 'background.paper'), 
        background: themeVars.isCustomBg ? widgetConfig.color : undefined,
        borderRadius: 3, 
        border: themeVars.isSeamlessCardMode || themeVars.isGradientTheme ? 'none' : '1px solid #e0e0e0', 
        boxShadow: themeVars.isSeamlessCardMode ? 'none' : '0 2px 10px rgba(0,0,0,0.05)',
        overflow: 'hidden', 
        cursor: isEditMode ? 'grab' : 'pointer', 
        transition: 'all 0.2s ease-in-out',
        '&:hover': isEditMode ? {} : {
          boxShadow: themeVars.isSeamlessCardMode ? 'none' : (themeVars.isGradientTheme ? '0 8px 24px rgba(0,0,0,0.15)' : '0 8px 24px rgba(0,122,255,0.12)'),
          borderColor: themeVars.isSeamlessCardMode || themeVars.isGradientTheme ? 'transparent' : '#b3d4ff',
          transform: themeVars.isSeamlessCardMode ? 'none' : 'translateY(-2px)' 
        }
      }}
    >
      {!isShortcutLike && (
        <Box sx={{ px: themeVars.isSeamlessCardMode ? 0 : 2, pt: 1.5, pb: themeVars.isSeamlessCardMode ? 1 : 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', bgcolor: 'transparent' }}>
          
          {/* 👇 KHU VỰC ĐÃ CẬP NHẬT: Thêm Tooltip, Icon và Hover Effect */}
          <Tooltip title="Nhấn để xem trang dữ liệu chi tiết" arrow placement="top-start">
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.75, 
                flex: 1, 
                minWidth: 0, // Cực kỳ quan trọng để thuộc tính noWrap bên trong hoạt động đúng với flexbox
                mr: 1,
                '&:hover .header-text, &:hover .header-icon': { 
                  color: themeVars.isCustomBg ? themeVars.themeTextColor : '#007aff',
                },
                '&:hover .header-icon': {
                  opacity: 1,
                  transform: 'translateY(-1px) translateX(1px)' // Mũi tên nhẹ nhích lên báo hiệu mở trang
                }
              }}
            >
              {!isEditMode && (
                <OpenInNewIcon 
                  className="header-icon"
                  sx={{ 
                    fontSize: themeVars.isSeamlessCardMode ? '1.1rem' : '0.9rem', 
                    color: themeVars.isSeamlessCardMode ? "text.primary" : (themeVars.isCustomBg ? themeVars.themeTextColor : "text.secondary"),
                    opacity: 0.4,
                    transition: 'all 0.2s ease-in-out',
                    flexShrink: 0
                  }} 
                />
              )}
              
              <Typography 
                className="header-text"
                variant={themeVars.isSeamlessCardMode ? "h6" : "subtitle2"} 
                fontWeight={themeVars.isSeamlessCardMode ? 800 : "bold"} 
                color={themeVars.isSeamlessCardMode ? "text.primary" : (themeVars.isCustomBg ? themeVars.themeTextColor : "text.secondary")} 
                sx={{ 
                  textTransform: themeVars.isSeamlessCardMode ? 'none' : 'uppercase', 
                  fontSize: themeVars.isSeamlessCardMode ? '1.1rem' : '0.75rem', 
                  letterSpacing: themeVars.isSeamlessCardMode ? 0 : '0.5px',
                  transition: 'color 0.2s ease-in-out',
                }} 
                noWrap
              >
                {title}
              </Typography>
              {!!nhanCongTy && !thanhCongTy && (
                <Chip size="small" icon={<BusinessIcon />} label={nhanCongTy} title={`Dữ liệu của ${nhanCongTy}`}
                  sx={{ height: 20, fontSize: '0.68rem', maxWidth: 180, flexShrink: 1, '& .MuiChip-icon': { fontSize: '0.8rem' } }} />
              )}
            </Box>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: -0.5, mr: themeVars.isSeamlessCardMode ? 0 : -1, flexShrink: 0 }}>
            {!isEditMode && (
              <>
                {type === "list" && widgetConfig?.canAdd !== false && (
                  <Tooltip title="Thêm dữ liệu">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); if(refList.current){ refList.current.openAddForm(); } }} sx={{ color: themeVars.isCustomBg ? themeVars.themeTextColor : 'text.secondary', opacity: themeVars.isGradientTheme ? 0.7 : 1, '&:hover': { color: themeVars.isCustomBg ? themeVars.themeTextColor : '#007aff', opacity: 1, bgcolor: themeVars.isGradientTheme ? 'rgba(255,255,255,0.3)' : '#e3f2fd' } }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Làm mới">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setRefreshTrigger(prev => prev + 1); }} sx={{ color: themeVars.isCustomBg ? themeVars.themeTextColor : 'text.secondary', opacity: themeVars.isGradientTheme ? 0.7 : 1, '&:hover': { color: themeVars.isCustomBg ? themeVars.themeTextColor : '#007aff', opacity: 1, bgcolor: themeVars.isGradientTheme ? 'rgba(255,255,255,0.3)' : '#e3f2fd' } }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {isEditMode && onRemove && (
              <Tooltip title="Xóa khỏi Dashboard">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} sx={{ color: 'error.main', bgcolor: '#ffebee' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      {isShortcutLike && (
         <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 12, display: 'flex', gap: 0.5 }}>
            {!isEditMode && type === 'counter' && (
              <Tooltip title="Làm mới">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setRefreshTrigger(prev => prev + 1); }} sx={{ color: themeVars.isCustomBg ? themeVars.themeTextColor : 'text.secondary', opacity: 0.3, '&:hover': { opacity: 1, bgcolor: themeVars.isGradientTheme ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.05)' } }}>
                  <RefreshIcon fontSize="18px" />
                </IconButton>
              </Tooltip>
            )}
            {isEditMode && onRemove && (
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()} sx={{ color: 'error.main', bgcolor: 'rgba(255,255,255,0.9)', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
         </Box>
      )}

      {thanhCongTy && !isShortcutLike && thanhCongTy}

      {/* Vùng Content Chính */}
      <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {!isEditMode && !['pivot', 'list', 'workflow','flow','report','custom'].includes(type) && <Box sx={{ position: 'absolute', inset: 0, zIndex: 5 }} />}
        {renderWidgetContent()}
        {isEditMode && <Box sx={{ position: 'absolute', inset: 0, zIndex: 10 }} />}
      </Box>

    </Box>
  );
};

// ================= BỌC THEO CÔNG TY =================
/** Thanh chọn công ty của widget nhiều công ty (mỗi lần xem một công ty). */
const ThanhChonCongTy = ({ ds, idChon, onChon, tenCongTy }) => (
  <Box
    onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}
    sx={{ display: 'flex', gap: 0.5, px: 2, pt: 1, pb: 0.5, overflowX: 'auto', flexShrink: 0, cursor: 'auto', ...macScrollbarSx }}
  >
    {ds.map((id) => (
      <Chip
        key={id} size="small" clickable
        icon={<BusinessIcon />}
        label={tenCongTy(id)}
        color={id === idChon ? 'primary' : 'default'}
        variant={id === idChon ? 'filled' : 'outlined'}
        onClick={() => onChon(id)}
        sx={{ height: 22, fontSize: '0.7rem', maxWidth: 200, '& .MuiChip-icon': { fontSize: '0.85rem' } }}
      />
    ))}
  </Box>
);

/**
 * Lớp ngoài của widget: đọc bộ lọc công ty chung của dashboard (nếu đang chọn) hoặc `widgetConfig.id_apps` rồi cho widget chạy trong ngữ cảnh của công ty tương ứng.
 * Không khai (hoặc chỉ khai công ty đang đăng nhập) -> đi thẳng vào WidgetDispatcher như trước.
 */
const WidgetTheoCongTy = (props) => {
  const { widget } = props;
  const context = useContext(AuthContext);
  const goc = context?.nguCanhGoc || context;
  const idHienTai = goc?.appInfo?._id ? String(goc.appInfo._id) : '';
  // Bộ lọc công ty chung ở đầu dashboard (null = mặc định, widget theo cấu hình riêng).
  const locCongTy = useContext(LocCongTyContext);
  const can = canPhamViCongTyHieuLuc(widget, idHienTai, locCongTy);
  const ds = useMemo(() => (can ? dsCongTyHieuLuc(widget, idHienTai, locCongTy) : []), [can, widget, idHienTai, locCongTy]);
  const [idChon, setIdChon] = useState(null);
  const tenCongTy = useTenCongTy(can);

  if (!can) return <WidgetDispatcher {...props} />;

  // Counter nhiều công ty: CỘNG TỔNG (mỗi công ty tự đếm trong phạm vi của nó).
  if (ds.length > 1 && widget.type === 'counter') {
    return <WidgetDispatcher {...props} ngoaiCongTy={khoaChuyenTrang(null, idHienTai, true)} dsCongTyTong={ds} nhanCongTy={`Tổng ${ds.length} công ty`} />;
  }

  const id = ds.includes(idChon) ? idChon : ds[0];
  const nhan = tenCongTy(id);
  const thanh = ds.length > 1 ? <ThanhChonCongTy ds={ds} idChon={id} onChon={setIdChon} tenCongTy={tenCongTy} /> : null;
  // Chỉ khoá chuyển trang khi công ty ĐANG XEM khác công ty đang đăng nhập (thanh chọn có thể đang ở đúng công ty này).
  const chung = { ...props, ngoaiCongTy: khoaChuyenTrang(id, idHienTai), nhanCongTy: nhan, thanhCongTy: thanh };
  return (
    <PhamViCongTy
      key={id}
      id_app={id}
      renderRunning={() => <WidgetDispatcher {...chung} trangThaiCongTy={{ dangTai: true }} />}
      renderError={(e) => <WidgetDispatcher {...chung} trangThaiCongTy={{ loi: (e && (e.message || e.error)) || String(e) }} />}
    >
      <WidgetDispatcher {...chung} />
    </PhamViCongTy>
  );
};

export default memo(WidgetTheoCongTy);